import { useCallback, useMemo } from 'react'

import { useIntl } from 'react-intl'

import { FormattedMessage } from '@b-stock/fe-core/bstock-react'

import { useListingSearch } from '@/components/listings/search/ListingSearchProvider'
import toDisplayNames from '@/components/listings/search/SearchFilters/toDisplayNames'

import AppliedFilterSection from '../../shared/AppliedFilterSection'
import CategoricalFilter from '../../shared/CategoricalFilter'
import { useFilterOptions } from '../../shared/filterUtils'
import type { AppliedProps, ListingFilter, FilterProps } from '../../types'
import {
  CategoryThresholdControl,
  LotCompositionPanel,
  useStagedCategoryThreshold,
  useTrackCategoryFilterApplied,
} from './CategoryThresholdControl'

const toDisplayName = toDisplayNames.categories

const CategoryFilter: ListingFilter<Set<string>> = ({
  onChange,
  desktopScreen,
  items: itemsData,
  value,
}: FilterProps<Set<string>>) => {
  const intl = useIntl()
  const items = useFilterOptions(itemsData, toDisplayName)
  const { state } = useListingSearch()
  const trackCategoryFilterApplied = useTrackCategoryFilterApplied()
  // In the category modal the slider is staged and applied together with the
  // staged categories on "Show results".
  const threshold = useStagedCategoryThreshold()

  const handleChange = useCallback(
    (newValue: Set<string>) => {
      trackCategoryFilterApplied({
        categories: Array.from(newValue),
        subcategories: state.filters.category,
        stagedThreshold: threshold.staged,
      })
      onChange(newValue)
    },
    [
      onChange,
      trackCategoryFilterApplied,
      threshold.staged,
      state.filters.category,
    ]
  )

  return (
    <>
      <CategoricalFilter
        alwaysUseModal
        items={items}
        name={intl.formatMessage({
          id: 'Filters.category',
        })}
        selected={value}
        onChange={handleChange}
        desktopScreen={desktopScreen}
        renderModalPanel={
          threshold.enabled
            ? (modalSelected) => (
                <LotCompositionPanel
                  variant="modal"
                  value={threshold.value}
                  onCommit={threshold.handleChange}
                  disabled={modalSelected.size === 0}
                />
              )
            : undefined
        }
        modalSectionTitle={
          <FormattedMessage id="Filters.modal.categoriesTitle" />
        }
        renderModalSummaryNote={(modalSelected) =>
          modalSelected.size > 0 && (
            <FormattedMessage
              id="Listing.filters.categoryThreshold.summaryNote"
              values={{ threshold: threshold.value }}
            />
          )
        }
        onModalSubmit={threshold.handleCommit}
        onModalReset={threshold.handleReset}
      />
      {/* The filters modal renders a staged panel above the list instead */}
      {desktopScreen && value.size > 0 && <CategoryThresholdControl />}
    </>
  )
}

CategoryFilter.expandedOnInit = true
CategoryFilter.hasCategoryThreshold = true
CategoryFilter.getOwnProps = ({ state, data, updateFilters }) => {
  return {
    filterKey: 'categories',
    items: data?.allFilters.categories || [],
    value: new Set(state.filters.categories),
    onChange: (value: Set<string>) => updateFilters({ categories: [...value] }),
  }
}

CategoryFilter.label = 'Filters.category'

const CategoryFilterApplied = ({
  filtersState,
  updateFilters,
  noClose,
}: AppliedProps) => {
  const topLevelValues = useMemo(
    () =>
      filtersState.categories.map((cat) => ({
        key: `c:${cat}`,
        name: toDisplayName(cat),
      })),
    [filtersState.categories]
  )

  const subcategoryValues = useMemo(
    () =>
      filtersState.category.map((sub) => ({
        key: `s:${sub}`,
        name: sub,
      })),
    [filtersState.category]
  )

  const values = useMemo(
    () => [...topLevelValues, ...subcategoryValues],
    [topLevelValues, subcategoryValues]
  )

  const onRemove = useCallback(
    (keyToRemove: string) => {
      if (keyToRemove.startsWith('c:')) {
        const cat = keyToRemove.slice(2)
        updateFilters({
          categories: filtersState.categories.filter((k) => k !== cat),
        })
      } else {
        const sub = keyToRemove.slice(2)
        updateFilters({
          category: filtersState.category.filter((k) => k !== sub),
        })
      }
    },
    [filtersState.categories, filtersState.category, updateFilters]
  )

  return (
    <AppliedFilterSection
      label="Filters.category"
      values={values}
      onRemove={onRemove}
      noClose={noClose}
    />
  )
}

CategoryFilter.Applied = CategoryFilterApplied

export default CategoryFilter
